// ============================================================================
// PhotoStrip — rotating horizontal photo grid for the homepage hero.
// ============================================================================
// Shows 6 images at a time, cycles through ~30 curated workshop/farm
// photos. Rotation interval defaults to 8 seconds. Crossfades between
// pages so the transition is smooth.
//
// Image manifest is hardcoded below — the actual files live at
// frontend/public/photos/<filename>. To add/remove/rename, update both
// the manifest here AND the file on disk.
//
// Responsive: 3 columns on mobile, 6 on desktop.
// ============================================================================

import { useEffect, useState } from 'react';

// 30 curated images from David's Dropbox photo libraries
// (UIF Staff Photos\Working Photos\Photos + Pics for Website).
// Filenames are URL-safe (lowercase, dashes) — see the PowerShell copy
// script David runs to populate frontend/public/photos/.
const PHOTOS = [
  'working-the-tiller.jpg',
  'team-chulahoma.jpg',
  'gliding-the-drip-tape.jpg',
  'tony-jones.jpg',
  'mulched.jpg',
  'teamwork-01.jpg',
  'teamwork-02.jpg',
  'bed-shaping.jpg',
  'cleaning.jpg',
  'opportunity-01.jpg',
  'growth-01.jpg',
  'img-1572.jpg',
  'img-1585.jpg',
  'calvin-head.jpg',
  'soil-testing-01.jpg',
  'soil-testing-02.jpg',
  'soil-testing-03.jpg',
  'crop-planning-01.jpg',
  'crop-planting-01.jpg',
  'chris-friedman.jpg',
  'keishas-farm.jpg',
  'angela-williams.jpg',
  'high-tunnel-01.jpg',
  'high-tunnel-02.jpg',
  'erosion-01.jpg',
  'soil-fertility-01.jpg',
  'farmscaping-01.jpg',
  'ecosystems-01.jpg',
  'crop-rotations-01.jpg',
  'animal-care-01.jpg',
];

const PER_PAGE = 6;
const ROTATE_MS = 8000;

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export function PhotoStrip() {
  const pages = chunk(PHOTOS, PER_PAGE);
  const [pageIdx, setPageIdx] = useState(0);

  useEffect(() => {
    if (pages.length <= 1) return;
    const interval = setInterval(() => {
      setPageIdx((i) => (i + 1) % pages.length);
    }, ROTATE_MS);
    return () => clearInterval(interval);
  }, [pages.length]);

  const current = pages[pageIdx] ?? [];

  return (
    <div
      className="grid grid-cols-3 md:grid-cols-6 gap-2"
      aria-label="Workshop photo collage"
    >
      {current.map((file) => (
        <div
          key={file}
          className="relative aspect-square overflow-hidden rounded-md bg-white/10"
        >
          <img
            src={`/photos/${file}`}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
            // Fade in when src changes — key on parent re-mounts the img on page change
            onError={(e) => {
              // If a file is missing, hide gracefully so the strip doesn't show broken icons
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      ))}
    </div>
  );
}
